import { getConfig } from "../../utils/environment.ts";
import {
  HYPER_RESOLUTION_STYLE_MODEL_UUID,
  PHOTO_REALISTIC_STYLE_MODEL_UUID,
} from "../constants.ts";
import {
  BLANK_PRESET,
  BRAND_3D_PAPERWORLD_ICON_PRESET,
  BRAND_COLLAGE_ICON_PRESET,
  BRAND_EXPAND_HERO_PRESET,
  BRAND_EXTEND_ICON_BANK_PRESET,
  BRAND_HERO_SATURATION_PRESET,
  BRAND_MINIMAL_BLUE_LOGO_PRESET,
  BRAND_MONO_MOSAIC_BG_PRESET,
  BRAND_PASTEL_OASIS_PATTERN_PRESET,
  BRAND_UPSCALE_PRESET,
  FROM_INSPIRATION_CONTENT_AS_CURATOR_PRESET,
  FROM_INSPIRATION_PRESET,
  FROM_PURCHASED_ASSET_PRESET,
  MOODBOARD_3D_GEOMETRIC_SHAPES_PRESET,
  MOODBOARD_BRAND_COLLECTION_PRESET,
  MOODBOARD_BW_HIGH_CONTRAST_PRESET,
  MOODBOARD_COLORFUL_LIVELY_PRESET,
  MOODBOARD_NEON_NIGHT_PHOTOGRAPHY_PRESET,
  MOODBOARD_PASTEL_PHOTOS_PRESET,
  MOODBOARD_RETRO_VIBES_PRESET,
  MOODBOARD_VINTAGE_SEPIA_PRESET,
  MOODBOARD_WARM_COZY_ILLUSTRATION_PRESET,
  NEW_BLANK_PROJECT_PRESET,
  OUTDOOR_ILLUSTRATION_WATERCOLOR_FOREST_PRESET,
  OUTDOOR_MOSAIC_LAKE_SUNSET_PRESET,
  OUTDOOR_PHOTO_ANIME_BEACH_PRESET,
  OUTDOOR_PHOTO_EDIT_SKY_SUNSET_PRESET,
  OUTDOOR_PHOTO_EXPAND_PANORAMA_PRESET,
  OUTDOOR_PHOTO_FRIENDS_SUNSET_PRESET,
  OUTDOOR_PHOTO_MODEL_CITY_PARK_PRESET,
  OUTDOOR_PHOTO_PRODUCT_NATURE_TABLE_PRESET,
  OUTDOOR_PHOTO_SHOES_URBAN_STEPS_PRESET,
  OUTDOOR_PHOTO_SHOOT_ARCHITECTURE_PRESET,
  OUTDOOR_PHOTO_SHOOT_FASHION_EDITORIAL_PRESET,
  OUTDOOR_PHOTO_SHOOT_OBJECT_WITH_MODEL_SHOWCASE_PRESET,
  OUTDOOR_PHOTO_UPSCALE_PRESET,
  PORTRAIT_ASTRID_RETRO_VIBES_PRESET,
  PORTRAIT_BEN_UPSCALE_PRESET,
  PORTRAIT_BLACK_AND_WHITE_PRESET,
  PORTRAIT_DOUBLE_EXPOSURE_PRESET,
  PORTRAIT_FISHERMAN_MOSAIC_PRESET,
  PORTRAIT_HANNAH_FISHEYE_CERAMIC_PRESET,
  PORTRAIT_JOHN_EXPAND_PRESET,
  PORTRAIT_JOHN_WHITE_SHIRT_PRESET,
  PORTRAIT_STUDIO_CLASSIC_PRESET,
  PORTRAIT_URBAN_PRESET,
  PORTRAIT_WATERCOLOR_PRESET,
  PRODUCT_SHOT_3D_OBJECT_STUDIO_PRESET,
  PRODUCT_SHOT_3D_TOTE_BAG_STUDIO_PRESET,
  PRODUCT_SHOT_COLOR_VARIANT_SHOT_PRESET,
  PRODUCT_SHOT_DIEGO_JACKET_SHOT_PRESET,
  PRODUCT_SHOT_EXPAND_PRESET,
  PRODUCT_SHOT_EXTERIOR_SHOT_PRESET,
  PRODUCT_SHOT_GLASSES_NEW_YORK_ELECTRIC_PRESET,
  PRODUCT_SHOT_GLASSES_RETRO_PARADISE_PRESET,
  PRODUCT_SHOT_GLASSES_SHOT_PRESET,
  PRODUCT_SHOT_SIMPLE_STUDIO_SHOT_PRESET,
  PRODUCT_SHOT_SMARTPHONE_PRESET,
  PRODUCT_SHOT_UPSCALE_PRESET,
  SOCIAL_AD_BRIEF_BRAND_CAMPAIGN_PRESET,
  SOCIAL_AD_CHARACTER_PRODUCT_REF_PRESET,
  SOCIAL_AD_EDIT_EXISTING_PRESET,
  SOCIAL_AD_EXPAND_STORY_PRESET,
  SOCIAL_AD_PRODUCT_PROMO_PRESET,
  SOCIAL_AD_STYLE_REF_PRESET,
  SOCIAL_AD_UPSCALE_HD_PRESET,
  STOCK_3D_SHAPES_PRESET,
  STOCK_BRAINSTORMING_PRESET,
  STOCK_BREAKFAST_EDIT_PRESET,
  STOCK_BREAKFAST_PRESET,
  STOCK_CARTOON_BACKGROUND_EDIT_PRESET,
  STOCK_CARTOON_CHARACTER_PRESET,
  STOCK_FOREST_EXPAND_PRESET,
  STOCK_FOREST_PRESET,
  STOCK_URBAN_PRESET,
  STOCK_WOODSHOP_PRESET,
  STUDIO_CHARACTER_PORTRAIT_PRESET,
  STUDIO_CINEMATIC_MONOCHROME_PRESET,
  STUDIO_COSMETIC_PRODUCT_PRESET,
  STUDIO_EDITORIAL_CHARACTER_PRESET,
  STUDIO_EXPAND_PRESET,
  STUDIO_FOOD_EDITORIAL_PRESET,
  STUDIO_NEOPOP_BAG_PRESET,
  STUDIO_OBJECT_STILL_LIFE_PRESET,
  STUDIO_PASTEL_OBJECT_PRESET,
  STUDIO_SHOES_HYPERREAL_PRESET,
  STUDIO_SOFT_FASHION_PRESET,
  STUDIO_UPSCALE_PRESET,
  STUDIO_WATERCOLOR_UNIVERSE_PRESET,
} from "./constants.ts";
import type { BoardState, StatePreset } from "./types.ts";

// XXX: Preset application behavior:
// - When key present: Override the current value with the preset value
//   Example: If preset has {modelUuids: ["abc123"]}, it will replace any existing selected models
// - When key present with undefined: Reset to the default value
//   Example: If preset has {selectedAssetUuid: undefined}, it will clear any selected asset
// - When key absent: Preserve the existing configuration value
//   Example: If preset doesn't specify defaultGenerationRatio, the current ratio will be maintained
// This allows presets to selectively modify board state properties while preserving unspecified configurations.

export const BOARD_STATE_BY_PRESET: Record<StatePreset, Partial<BoardState>> = {
  // Generic presets
  [NEW_BLANK_PROJECT_PRESET]: {
    // We explicitly set the model picker state to `null` to avoid having the model picker search key in the URL
    modelPickerState: {
      isOpen: null,
    },
    selectedAssetUuid: undefined,
    modelUuids: [],
    defaultTool: {
      defaultCreateTool: "generate",
      defaultEditTool: undefined,
    },
  },
  [FROM_PURCHASED_ASSET_PRESET]: {
    // We explicitly set the model picker state to `null` to avoid having the model picker search key in the URL
    modelPickerState: {
      isOpen: null,
    },
  },
  [FROM_INSPIRATION_CONTENT_AS_CURATOR_PRESET]: {
    // We explicitly set the model picker state to `null` to avoid having the model picker search key in the URL
    modelPickerState: {
      isOpen: null,
    },
  },
  [FROM_INSPIRATION_PRESET]: {
    // We explicitly set the model picker state to `null` to avoid having the model picker search key in the URL
    modelPickerState: {
      isOpen: null,
    },
    defaultTool: {
      defaultCreateTool: "generate",
      defaultEditTool: undefined,
    },
  },
  [BLANK_PRESET]: {
    selectedAssetUuid: undefined,
    // We explicitly set the model picker state to `null` to avoid having the model picker search key in the URL
    modelPickerState: {
      isOpen: null,
    },
    defaultTool: {
      defaultCreateTool: "generate",
      defaultEditTool: undefined,
    },
  },

  // Quick actions presets

  // Product shots
  [PRODUCT_SHOT_SMARTPHONE_PRESET]: {
    selectedAssetUuid: undefined,
    modelPickerState: {
      isOpen: null,
    },
    defaultTool: {
      defaultCreateTool: "generate",
      defaultEditTool: undefined,
    },
    modelUuids: [HYPER_RESOLUTION_STYLE_MODEL_UUID],
    defaultGenerationRatio: "r_1_1",
    prompt:
      "A sleek modern smartphone displayed on a minimalist white surface with geometric shadows. The device shows a vibrant screen interface, positioned at a slight angle to catch studio lighting. The background features subtle gradients from white to light gray, creating depth without distraction. Professional product photography style with crisp details and perfect reflections.",
  },
  [PRODUCT_SHOT_SIMPLE_STUDIO_SHOT_PRESET]: {
    selectedAssetUuid: undefined,
    modelPickerState: {
      isOpen: null,
    },
    defaultTool: {
      defaultCreateTool: "generate",
      defaultEditTool: undefined,
    },
    modelUuids: getConfig({
      dev: [],
      staging: [],
      // Studio Shot - Object
      prod: ["de0c7fc2-3187-471e-a8ef-b3baa37c2a06"],
    }),
    defaultGenerationRatio: "r_1_1",
    prompt:
      "A high-resolution professional photo of a cosmetic product (e.g., face cream jar) isolated on a pristine white background, studio lighting, no shadows, ready for ecommerce.",
  },
  [PRODUCT_SHOT_EXTERIOR_SHOT_PRESET]: {
    selectedAssetUuid: undefined,
    modelPickerState: {
      isOpen: null,
    },
    defaultTool: {
      defaultCreateTool: "generate",
      defaultEditTool: undefined,
    },
    modelUuids: getConfig({
      dev: [],
      staging: [],
      // Exterior shot (exists only in production for now)
      prod: ["4e80b5e1-1b5c-49c3-9b9b-3b4ad1397f6e"],
    }),
    defaultGenerationRatio: "r_16_9",
    prompt:
      "A bottle of perfume elegantly placed on an outdoor café table, with blurred city background, natural daylight, and Parisian vibes.",
  },
  [PRODUCT_SHOT_COLOR_VARIANT_SHOT_PRESET]: {
    modelPickerState: {
      isOpen: null,
    },
    defaultTool: {
      defaultCreateTool: "generate",
      defaultEditTool: "edit-image-with-prompt",
    },
    modelUuids: [],
    prompt: "Change the color of the shoes to pastel pink.",
  },
  [PRODUCT_SHOT_3D_OBJECT_STUDIO_PRESET]: {
    selectedAssetUuid: undefined,
    modelPickerState: {
      isOpen: null,
    },
    defaultTool: {
      defaultCreateTool: "generate",
      defaultEditTool: undefined,
    },
    modelUuids: getConfig({
      dev: [],
      staging: [],
      // Studio Shot - Object
      prod: ["de0c7fc2-3187-471e-a8ef-b3baa37c2a06"],
    }),
    defaultGenerationRatio: "r_1_1",
    prompt:
      "A 3D render of a sneaker floating with splashes of colorful paint, dramatic studio lighting, white seamless backdrop.",
  },
  [PRODUCT_SHOT_3D_TOTE_BAG_STUDIO_PRESET]: {
    selectedAssetUuid: undefined,
    modelPickerState: {
      isOpen: null,
    },
    defaultTool: {
      defaultCreateTool: "generate",
      defaultEditTool: undefined,
    },
    modelUuids: getConfig({
      dev: [],
      staging: [],
      prod: [
        "de0c7fc2-3187-471e-a8ef-b3baa37c2a06",
        "0cd0e2b4-eebc-4479-a0a5-def55171c3ab",
      ],
    }),
    defaultGenerationRatio: "r_1_1",
    prompt:
      "A luxury tote bag placed on a marble pedestal, soft natural daylight, minimal background.",
  },
  [PRODUCT_SHOT_DIEGO_JACKET_SHOT_PRESET]: {
    selectedAssetUuid: undefined,
    modelPickerState: { isOpen: null },
    defaultTool: {
      defaultCreateTool: "generate",
      defaultEditTool: undefined,
    },
    modelUuids: getConfig({
      dev: [],
      staging: [],
      prod: [
        "56792d79-21a7-41be-b898-f6876f6e5000",
        "01f53041-08d6-460d-8467-a122388aaabc",
      ], // Diego and Jacket
    }),
    defaultGenerationRatio: "r_4_5",
    prompt:
      "A high-fashion studio shot of a man wearing a stylish jacket. Minimalist pale backdrop, dramatic lighting, and both the character and the jacket are in sharp focus, suitable for an ecommerce hero shot.",
  },
  [PRODUCT_SHOT_GLASSES_SHOT_PRESET]: {
    selectedAssetUuid: undefined,
    modelPickerState: { isOpen: null },
    defaultTool: {
      defaultCreateTool: "generate",
      defaultEditTool: undefined,
    },
    modelUuids: getConfig({
      dev: [],
      staging: [],
      prod: [
        "f3cfab1a-aef5-4baa-b2e1-c3c0d5eea74c",
        "da48d81d-7c3c-4c57-a22c-e391bfbde211",
      ], // Studio Shot and Glasses
    }),
    defaultGenerationRatio: "r_1_1",
    prompt:
      "A high-resolution studio photograph of stylish modern glasses displayed on a white pedestal with soft, even lighting and crisp shadows. The scene is minimalist, elegant, and ready for use in a fashion e-commerce catalog.",
  },
  [PRODUCT_SHOT_GLASSES_NEW_YORK_ELECTRIC_PRESET]: {
    selectedAssetUuid: undefined,
    modelPickerState: { isOpen: null },
    defaultTool: {
      defaultCreateTool: "generate",
      defaultEditTool: undefined,
    },
    modelUuids: getConfig({
      dev: [],
      staging: [],
      prod: [
        "93b391a9-5962-4baa-abd1-91544b5bf71f",
        "da48d81d-7c3c-4c57-a22c-e391bfbde211",
      ], // New York Electric and Glasses
    }),
    defaultGenerationRatio: "r_16_9",
    prompt:
      "A close-up photo of trendy glasses positioned on a glossy surface under vibrant neon lights, with colorful reflections and an urban nightlife vibe. Modern, bold, and youthful aesthetic.",
  },
  [PRODUCT_SHOT_GLASSES_RETRO_PARADISE_PRESET]: {
    selectedAssetUuid: undefined,
    modelPickerState: { isOpen: null },
    defaultTool: {
      defaultCreateTool: "generate",
      defaultEditTool: undefined,
    },
    modelUuids: getConfig({
      dev: [],
      staging: [],
      prod: [
        "2fa9963b-9f0c-48bf-9d3f-fe084feebde0",
        "da48d81d-7c3c-4c57-a22c-e391bfbde211",
      ], // Retro Paradise and Glasses
    }),
    defaultGenerationRatio: "r_4_5",
    prompt:
      "A colorful, retro-inspired 2D illustration of a pair of classic glasses resting on a pastel geometric shape, with playful patterns and a cheerful vibe. Perfect for a vintage eyewear launch.",
  },
  [PRODUCT_SHOT_UPSCALE_PRESET]: {
    modelPickerState: {
      isOpen: null,
    },
    defaultTool: {
      defaultCreateTool: undefined,
      defaultEditTool: "upscale",
    },
    modelUuids: [],
  },
  [PRODUCT_SHOT_EXPAND_PRESET]: {
    modelPickerState: {
      isOpen: null,
    },
    defaultTool: {
      defaultCreateTool: undefined,
      defaultEditTool: "expand",
    },
    modelUuids: [],
    defaultGenerationRatio: "r_16_9",
  },

  // Outdoor photoshoot
  [OUTDOOR_PHOTO_SHOOT_FASHION_EDITORIAL_PRESET]: {
    selectedAssetUuid: undefined,
    modelPickerState: {
      isOpen: null,
    },
    defaultTool: {
      defaultCreateTool: "generate",
      defaultEditTool: undefined,
    },
    modelUuids: getConfig({
      dev: [],
      staging: [],
      // Chiaroscuro model (exists only in production for now)
      prod: ["9f065df4-69cf-4bc8-8872-7a51bc843a89"],
    }),
    defaultGenerationRatio: "r_4_5",
    prompt:
      "A fashion model wearing an elegant black evening dress poses confidently in a modern urban setting during golden hour. The lighting creates dramatic shadows and highlights, emphasizing the fabric's texture and the model's silhouette. The background shows blurred city architecture with warm sunset tones, creating a sophisticated editorial atmosphere.",
  },
  [OUTDOOR_PHOTO_SHOOT_ARCHITECTURE_PRESET]: {
    selectedAssetUuid: undefined,
    modelPickerState: {
      isOpen: null,
    },
    defaultTool: {
      defaultCreateTool: "generate",
      defaultEditTool: undefined,
    },
    modelUuids: getConfig({
      dev: [],
      staging: [],
      // Saturation model (exists only in production for now)
      prod: ["21012147-c924-4fe9-9459-a6a9cee8381d"],
    }),
    defaultGenerationRatio: "r_16_9",
    prompt:
      "A stunning modern architectural building with clean geometric lines and floor-to-ceiling glass windows. The structure features a combination of concrete and steel elements, photographed during blue hour with interior lights glowing warmly against the twilight sky. The composition emphasizes the building's bold design and integration with the surrounding landscape.",
  },
  [OUTDOOR_PHOTO_SHOOT_OBJECT_WITH_MODEL_SHOWCASE_PRESET]: {
    selectedAssetUuid: undefined,
    modelPickerState: {
      isOpen: null,
    },
    defaultTool: {
      defaultCreateTool: "generate",
      defaultEditTool: undefined,
    },
    defaultGenerationRatio: "r_4_5",
    modelUuids: getConfig({
      dev: [],
      staging: [],
      // Totebag and Hannah models (exist only in production for now)
      prod: [
        "0cd0e2b4-eebc-4479-a0a5-def55171c3ab",
        "0700abd9-5829-4c37-95d5-e5e3bd1bf004",
      ],
    }),
    prompt:
      "A young female artist with dark hair in buns carries a fabric canvas tote bag filled with art supplies through a vibrant street market. Paintbrushes and sketchbooks peek out from the bag, while colorful paint tubes can be seen inside. The character, with paint stains on her hands, exudes a bohemian style and wears casual artistic clothing. The background showcases colorful street art and bustling creative energy, capturing the free-spirited lifestyle of the young artist community.",
  },
  [OUTDOOR_PHOTO_MODEL_CITY_PARK_PRESET]: {
    selectedAssetUuid: undefined,
    modelPickerState: {
      isOpen: null,
    },
    defaultTool: {
      defaultCreateTool: "generate",
      defaultEditTool: undefined,
    },
    modelUuids: getConfig({
      dev: [],
      staging: [],
      prod: [
        "29a9a666-6d79-4c92-97d0-7e16e216d91e", // "Astrid"
        "4e80b5e1-1b5c-49c3-9b9b-3b4ad1397f6e", // "Exterior shot"
      ],
    }),
    defaultGenerationRatio: "r_4_5",
    prompt:
      "A fashionable young woman in a floral summer dress and straw hat poses in a sunlit city park, lush green grass and trees in the background, candid editorial photo.",
  },
  [OUTDOOR_PHOTO_FRIENDS_SUNSET_PRESET]: {
    selectedAssetUuid: undefined,
    modelPickerState: {
      isOpen: null,
    },
    defaultTool: {
      defaultCreateTool: "generate",
    },
    modelUuids: getConfig({
      dev: [],
      staging: [],
      prod: [
        "6b1af07f-d3f1-480c-a291-fdd905d7d95a", // "Dark Urban Elegance"
      ],
    }),
    defaultGenerationRatio: "r_16_9",
    prompt:
      "A group of friends laughing and enjoying a picnic at sunset in a city park, golden hour lighting, tall buildings in the distance, authentic lifestyle photography.",
  },
  [OUTDOOR_PHOTO_PRODUCT_NATURE_TABLE_PRESET]: {
    selectedAssetUuid: undefined,
    modelPickerState: {
      isOpen: null,
    },
    defaultTool: {
      defaultCreateTool: "generate",
    },
    modelUuids: getConfig({
      dev: [],
      staging: [],
      prod: ["4e80b5e1-1b5c-49c3-9b9b-3b4ad1397f6e"], // "Exterior shot"
    }),
    defaultGenerationRatio: "r_4_5",
    prompt:
      "A beverage bottle product shot outdoors on a rustic wooden table, surrounded by green foliage, dappled sunlight, and a soft-focus natural background. Professional lifestyle advertising photo.",
  },
  [OUTDOOR_PHOTO_SHOES_URBAN_STEPS_PRESET]: {
    selectedAssetUuid: undefined,
    modelPickerState: {
      isOpen: null,
    },
    defaultTool: {
      defaultCreateTool: "generate",
    },
    modelUuids: getConfig({
      dev: [],
      staging: [],
      prod: ["4e80b5e1-1b5c-49c3-9b9b-3b4ad1397f6e"], // "Exterior shot"
    }),
    defaultGenerationRatio: "r_16_9",
    prompt:
      "Sleek sneakers placed on sunlit concrete steps in an urban park, with greenery and city buildings softly blurred in the background. Daytime, high-contrast real-life photo feel.",
  },
  [OUTDOOR_PHOTO_ANIME_BEACH_PRESET]: {
    selectedAssetUuid: undefined,
    modelPickerState: {
      isOpen: null,
    },
    defaultTool: {
      defaultCreateTool: "generate",
    },
    modelUuids: getConfig({
      dev: [],
      staging: [],
      prod: [
        "b110f02a-6e4b-40e5-9375-339ce6b9ec5c", // "Anime"
      ],
    }),
    defaultGenerationRatio: "r_16_9",
    prompt: "Sunny beach scene with waves, blue skies, and vibrant colors.",
  },
  [OUTDOOR_PHOTO_EDIT_SKY_SUNSET_PRESET]: {
    modelPickerState: {
      isOpen: null,
    },
    defaultTool: {
      defaultCreateTool: undefined,
      defaultEditTool: "edit-image-with-prompt",
    },
    modelUuids: [],
    prompt:
      "Replace the current sky with a vibrant sunset featuring golden and pink clouds.",
  },
  [OUTDOOR_ILLUSTRATION_WATERCOLOR_FOREST_PRESET]: {
    modelPickerState: {
      isOpen: null,
    },
    defaultTool: {
      defaultCreateTool: "generate",
    },
    modelUuids: getConfig({
      dev: [],
      staging: [],
      prod: ["ece7566a-8152-4e61-8ba6-4743b180491e"], // Watercolor Universe
    }),
    defaultGenerationRatio: "r_16_9",
    prompt:
      "A dreamy watercolor illustration of an enchanted forest at sunrise. Soft gradients, natural green and orange tones, whimsical trees with glowing leaves, and light fog swirling around the roots. The scene feels magical and painterly.",
  },
  [OUTDOOR_MOSAIC_LAKE_SUNSET_PRESET]: {
    modelPickerState: {
      isOpen: null,
    },
    defaultTool: {
      defaultCreateTool: "generate",
    },
    modelUuids: getConfig({
      dev: [],
      staging: [],
      prod: ["22be547f-71f4-4d65-86fe-eb6f82a88680"], // Mosaic
    }),
    defaultGenerationRatio: "r_16_9",
    prompt:
      "A stylized mosaic illustration of a calm lake reflecting a brilliant sunset. The sky features rich blues, warm golds, and deep reds in a geometric pattern, with dark silhouetted trees along the shore. Artistic, intricate, and striking.",
  },
  [OUTDOOR_PHOTO_EXPAND_PANORAMA_PRESET]: {
    modelPickerState: {
      isOpen: null,
    },
    defaultTool: {
      defaultCreateTool: undefined,
      defaultEditTool: "expand",
    },
    defaultGenerationRatio: "r_16_9",
    modelUuids: [],
  },
  [OUTDOOR_PHOTO_UPSCALE_PRESET]: {
    modelPickerState: {
      isOpen: null,
    },
    defaultTool: {
      defaultCreateTool: undefined,
      defaultEditTool: "upscale",
    },
    modelUuids: [],
  },

  // Moodboard presets
  [MOODBOARD_WARM_COZY_ILLUSTRATION_PRESET]: {
    modelPickerState: { isOpen: null },
    selectedAssetUuid: undefined,
    modelUuids: getConfig({
      dev: [],
      staging: [],
      prod: ["8ec087d6-02b1-4697-b8ac-9d56aa6ffdd4"], // Cozy Warm Illustrations
    }),
    prompt:
      "A warm, inviting digital set of illustrations showing soft and cozy situations under autumn colors.",
    defaultTool: { defaultCreateTool: "brief", defaultEditTool: undefined },
    defaultGenerationRatio: undefined,
  },
  [MOODBOARD_BRAND_COLLECTION_PRESET]: {
    modelPickerState: { isOpen: null },
    selectedAssetUuid: undefined,
    prompt:
      "Launching an Autumn collection for a lifestyle brand: the moodboard should show cozy scenes, knit textures, warm colors, foliage, and relaxed everyday activities.  Target: Young adults in urban settings.",
    modelUuids: [],
    defaultTool: { defaultCreateTool: "brief", defaultEditTool: undefined },
    defaultGenerationRatio: undefined,
  },
  [MOODBOARD_NEON_NIGHT_PHOTOGRAPHY_PRESET]: {
    modelPickerState: { isOpen: null },
    selectedAssetUuid: undefined,
    modelUuids: getConfig({
      dev: [],
      staging: [],
      prod: ["d232fccd-4395-4956-8f6d-4b329a637384"], // Neon Lights
    }),
    prompt:
      "Neon-lit cityscapes, dusk/nighttime, with glowing signs and vibrant light reflections. Street fashion, nightlife, and an energetic urban vibe.",
    defaultTool: { defaultCreateTool: "brief", defaultEditTool: undefined },
    defaultGenerationRatio: undefined,
  },

  [MOODBOARD_3D_GEOMETRIC_SHAPES_PRESET]: {
    modelPickerState: { isOpen: null },
    selectedAssetUuid: undefined,
    modelUuids: getConfig({
      dev: [],
      staging: [],
      prod: ["15e8f1c6-19c3-47d5-9280-59190c86338a"], // 3D Objects Studio
    }),
    prompt:
      "Modern moodboard of 3D geometric shapes—spheres, cubes, and pyramids—in pastel and metallic gradients, minimal composition. Futuristic design inspiration.",
    defaultTool: { defaultCreateTool: "brief", defaultEditTool: undefined },
    defaultGenerationRatio: undefined,
  },

  [MOODBOARD_PASTEL_PHOTOS_PRESET]: {
    modelPickerState: { isOpen: null },
    selectedAssetUuid: undefined,
    modelUuids: getConfig({
      dev: [],
      staging: [],
      prod: ["f7ca640a-60e9-49c7-a88a-9c68a312981c"], // Pastel Oasis
    }),
    prompt:
      "Soft pastel photography abstract shapes, delicate flowers, dreamy poolside scenes. Uplifting and gentle mood, ideal for wellness/lifestyle exploration.",
    defaultTool: { defaultCreateTool: "brief", defaultEditTool: undefined },
    defaultGenerationRatio: undefined,
  },
  [MOODBOARD_RETRO_VIBES_PRESET]: {
    modelPickerState: { isOpen: null },
    selectedAssetUuid: undefined,
    modelUuids: getConfig({
      dev: [],
      staging: [],
      prod: ["118bd441-bf05-4a32-a889-5c5a0f22b89f"], // Retro Vibes
    }),
    prompt:
      "Bold, retro-inspired photos with strong neon, palm trees, 80s style, and playful poses. Use rich turquoise, pink, orange, and yellow.",
    defaultTool: { defaultCreateTool: "brief", defaultEditTool: undefined },
    defaultGenerationRatio: undefined,
  },
  [MOODBOARD_BW_HIGH_CONTRAST_PRESET]: {
    modelPickerState: { isOpen: null },
    selectedAssetUuid: undefined,
    modelUuids: getConfig({
      dev: [],
      staging: [],
      prod: ["3b74ea2e-0d59-4a59-8e75-812211a467dd"], // Black and white illustration
    }),
    prompt:
      "Dramatic black and white moodboard: minimalist compositions, bold lines, graphic patterns, architectural photography, dynamic lighting, and hand-drawn sketches. Perfect for high-impact fashion or editorial inspiration.",
    defaultTool: { defaultCreateTool: "brief", defaultEditTool: undefined },
    defaultGenerationRatio: undefined,
  },
  [MOODBOARD_COLORFUL_LIVELY_PRESET]: {
    modelPickerState: { isOpen: null },
    selectedAssetUuid: undefined,
    modelUuids: getConfig({
      dev: [],
      staging: [],
      prod: ["6a476624-2b35-4ae2-9a86-838d6c4b14e8"], // Colorful
    }),
    prompt:
      "A moodboard filled with energy: bright and bold colors, expressive brush strokes, playful props, dynamic group scenes, and abstract patterns. Blue, red, orange, yellow, green, pink, teal—every color pops for creative exploration.",
    defaultTool: { defaultCreateTool: "brief", defaultEditTool: undefined },
    defaultGenerationRatio: undefined,
  },
  [MOODBOARD_VINTAGE_SEPIA_PRESET]: {
    modelPickerState: { isOpen: null },
    selectedAssetUuid: undefined,
    modelUuids: getConfig({
      dev: [],
      staging: [],
      prod: ["43fcb56d-93e8-4fa9-8ee0-5e100641bd55"], // Golden Memories (Sepia)
    }),
    prompt:
      "A vintage-inspired, sepia-toned moodboard with nostalgic family scenes, old photo textures, sunlit interiors, cozy objects, and golden hour light. Ideal for capturing timeless, sentimental, or heritage-driven branding.",
    defaultTool: { defaultCreateTool: "brief", defaultEditTool: undefined },
    defaultGenerationRatio: undefined,
  },
  // Brand assets presets
  [BRAND_EXTEND_ICON_BANK_PRESET]: {
    selectedAssetUuid: undefined,
    modelPickerState: {
      isOpen: null,
    },
    defaultTool: {
      defaultCreateTool: "brief",
      defaultEditTool: undefined,
    },
    modelUuids: getConfig({
      dev: [],
      staging: [],
      // Silhouette Set Icons model (exist only in production for now)
      prod: ["238b390a-983c-4c88-9255-6fdb64aa976e"],
    }),
    defaultGenerationRatio: "r_1_1",
    prompt:
      "I would like to generate a list of illustrations that I can used as a library for my website, it should contain all basic elements.",
  },
  [BRAND_MINIMAL_BLUE_LOGO_PRESET]: {
    selectedAssetUuid: undefined,
    modelPickerState: { isOpen: null },
    defaultTool: {
      defaultCreateTool: "generate",
      defaultEditTool: undefined,
    },
    modelUuids: getConfig({
      dev: [],
      staging: [],
      prod: ["2d247c70-0409-4cb8-a1a3-5d7c3bb856dd"], // Flat Blue Icons
    }),
    defaultGenerationRatio: "r_1_1",
    prompt:
      "A minimalist, modern brand logo centered on a flat soft blue background. Simple geometric icon, clean lines, clear readability. Suitable for tech or lifestyle brands.",
  },
  [BRAND_3D_PAPERWORLD_ICON_PRESET]: {
    selectedAssetUuid: undefined,
    modelPickerState: { isOpen: null },
    defaultTool: {
      defaultCreateTool: "generate",
      defaultEditTool: undefined,
    },
    modelUuids: getConfig({
      dev: [],
      staging: [],
      prod: ["8f74a250-f30f-4e5c-9d10-5e53b484743c"], // 3D Paper World
    }),
    defaultGenerationRatio: "r_1_1",
    prompt:
      "A set of 3D icons with warm orange and pastel tones, playful yet elegant, floating over a softly colored backdrop. Perfect for branding or UI design.",
  },
  [BRAND_COLLAGE_ICON_PRESET]: {
    selectedAssetUuid: undefined,
    modelPickerState: { isOpen: null },
    defaultTool: {
      defaultCreateTool: "generate",
      defaultEditTool: undefined,
    },
    modelUuids: getConfig({
      dev: [],
      staging: [],
      prod: ["c8ffac9a-e22d-4eaa-9862-bc2a1dc1956b"], // Collage Icons
    }),
    defaultGenerationRatio: "r_1_1",
    prompt:
      "A creative, hand-crafted collage icon using earthy tones and vibrant reds/blues. Abstract shapes and playful composition, perfect for a modern brand touch.",
  },
  [BRAND_HERO_SATURATION_PRESET]: {
    selectedAssetUuid: undefined,
    modelPickerState: { isOpen: null },
    defaultTool: {
      defaultCreateTool: "generate",
      defaultEditTool: undefined,
    },
    modelUuids: getConfig({
      dev: [],
      staging: [],
      prod: ["21012147-c924-4fe9-9459-a6a9cee8381d"], // Saturation
    }),
    defaultGenerationRatio: "r_16_9",
    prompt:
      "A dynamic and vibrant hero banner for a brand, full of neon orange, blue, and bright pink gradient splashes. Trendy, energetic, and engaging for modern digital campaigns.",
  },
  [BRAND_PASTEL_OASIS_PATTERN_PRESET]: {
    selectedAssetUuid: undefined,
    modelPickerState: { isOpen: null },
    defaultTool: {
      defaultCreateTool: "generate",
      defaultEditTool: undefined,
    },
    modelUuids: getConfig({
      dev: [],
      staging: [],
      prod: ["f7ca640a-60e9-49c7-a88a-9c68a312981c"], // Pastel Oasis
    }),
    defaultGenerationRatio: "r_16_9",
    prompt:
      "An abstract background full of pastel blue, pink, and mint geometric shapes. Soft gradients, organic lines, and subtle texture, perfect for brand social or presentations.",
  },
  [BRAND_MONO_MOSAIC_BG_PRESET]: {
    selectedAssetUuid: undefined,
    modelPickerState: { isOpen: null },
    defaultTool: {
      defaultCreateTool: "generate",
      defaultEditTool: undefined,
    },
    modelUuids: ["22be547f-71f4-4d65-86fe-eb6f82a88680"], // Mosaic
    defaultGenerationRatio: "r_16_9",
    prompt:
      "A sophisticated monochrome background in black, white, and deep grey, with a mosaic pattern. Modern, bold, and refined, suitable for brand identity and website banners.",
  },
  [BRAND_EXPAND_HERO_PRESET]: {
    modelPickerState: { isOpen: null },
    defaultTool: {
      defaultCreateTool: undefined,
      defaultEditTool: "expand",
    },
    defaultGenerationRatio: "r_16_9",
    modelUuids: [],
  },
  [BRAND_UPSCALE_PRESET]: {
    modelPickerState: { isOpen: null },
    defaultTool: {
      defaultCreateTool: undefined,
      defaultEditTool: "upscale",
    },
    modelUuids: [],
  },

  // Portraits
  [PORTRAIT_STUDIO_CLASSIC_PRESET]: {
    selectedAssetUuid: undefined,
    modelPickerState: {
      isOpen: null,
    },
    defaultTool: {
      defaultCreateTool: "generate",
      defaultEditTool: undefined,
    },
    modelUuids: getConfig({
      dev: [],
      staging: [],
      prod: ["f3cfab1a-aef5-4baa-b2e1-c3c0d5eea74c"],
    }), // Studio Shot
    defaultGenerationRatio: "r_3_4",
    prompt:
      "A professional studio portrait of a confident young adult, neutral background, soft natural lighting, sharp focus on the face.",
  },
  [PORTRAIT_URBAN_PRESET]: {
    selectedAssetUuid: undefined,
    modelPickerState: {
      isOpen: null,
    },
    defaultTool: {
      defaultCreateTool: "generate",
      defaultEditTool: undefined,
    },
    modelUuids: [PHOTO_REALISTIC_STYLE_MODEL_UUID],
    defaultGenerationRatio: "r_4_5",
    prompt:
      "A candid portrait of a trendy young man in the city, urban background, strong contrast, natural daylight.",
  },
  [PORTRAIT_BLACK_AND_WHITE_PRESET]: {
    selectedAssetUuid: undefined,
    modelPickerState: {
      isOpen: null,
    },
    defaultTool: {
      defaultCreateTool: "generate",
      defaultEditTool: undefined,
    },
    modelUuids: getConfig({
      dev: [],
      staging: [],
      prod: ["061ec6e4-e359-45d8-9558-ea2419a63f77"],
    }), // Cinematic Monochrome
    defaultGenerationRatio: "r_4_5",
    prompt:
      "A dramatic black and white portrait, high contrast lighting, intense expression, studio background.",
  },
  [PORTRAIT_WATERCOLOR_PRESET]: {
    selectedAssetUuid: undefined,
    modelPickerState: {
      isOpen: null,
    },
    defaultTool: {
      defaultCreateTool: "generate",
      defaultEditTool: undefined,
    },
    modelUuids: getConfig({
      dev: [],
      staging: [],
      prod: ["ece7566a-8152-4e61-8ba6-4743b180491e"],
    }), // Watercolor Universe
    defaultGenerationRatio: "r_1_1",
    prompt:
      "A whimsical watercolor illustration portrait of a joyful woman, expressive brush strokes, vivid gradients, artistic background.",
  },
  [PORTRAIT_DOUBLE_EXPOSURE_PRESET]: {
    selectedAssetUuid: undefined,
    modelPickerState: {
      isOpen: null,
    },
    defaultTool: {
      defaultCreateTool: "generate",
      defaultEditTool: undefined,
    },
    modelUuids: getConfig({
      dev: [],
      staging: [],
      prod: ["1e936054-4441-4f07-8874-bda662778ab6"],
    }), // Argentic
    defaultGenerationRatio: "r_3_4",
    prompt:
      "Portrait of a woman with her face blending into a misty forest landscape, dreamy double exposure effect, surreal and atmospheric.",
  },
  [PORTRAIT_FISHERMAN_MOSAIC_PRESET]: {
    selectedAssetUuid: undefined,
    modelPickerState: {
      isOpen: null,
    },
    defaultTool: {
      defaultCreateTool: "generate",
      defaultEditTool: undefined,
    },
    modelUuids: getConfig({
      dev: [],
      staging: [],
      prod: ["22be547f-71f4-4d65-86fe-eb6f82a88680"],
    }), // Mosaic
    defaultGenerationRatio: "r_3_4",
    prompt:
      "Abstract portrait of an old fisherman, face formed from colorful glass mosaic pieces, Cubist-inspired, rich textures and geometric patterns.",
  },
  [PORTRAIT_JOHN_WHITE_SHIRT_PRESET]: {
    selectedAssetUuid: undefined,
    modelPickerState: {
      isOpen: null,
    },
    defaultTool: {
      defaultCreateTool: "generate",
      defaultEditTool: undefined,
    },
    modelUuids: getConfig({
      dev: [],
      staging: [],
      prod: ["92ed6a47-a2a7-44fc-abcd-8abee650a4f4"],
    }), // John
    defaultGenerationRatio: "r_1_1",
    prompt:
      "A portrait of the man in a white shirt, clean background, subtle softbox lighting.",
  },
  [PORTRAIT_ASTRID_RETRO_VIBES_PRESET]: {
    selectedAssetUuid: undefined,
    modelPickerState: {
      isOpen: null,
    },
    defaultTool: {
      defaultCreateTool: "generate",
      defaultEditTool: undefined,
    },
    modelUuids: getConfig({
      dev: [],
      staging: [],
      prod: [
        "118bd441-bf05-4a32-a889-5c5a0f22b89f",
        "29a9a666-6d79-4c92-97d0-7e16e216d91e",
      ],
    }), // Retro Vibes and Astrid
    defaultGenerationRatio: "r_3_4",
    prompt:
      "A retro-inspired portrait of the girl, casual jacket, vibrant neon colors, trendy urban setting.",
  },
  [PORTRAIT_HANNAH_FISHEYE_CERAMIC_PRESET]: {
    selectedAssetUuid: undefined,
    modelPickerState: {
      isOpen: null,
    },
    defaultTool: {
      defaultCreateTool: "generate",
      defaultEditTool: undefined,
    },
    modelUuids: getConfig({
      dev: [],
      staging: [],
      prod: [
        "5ca0e43b-36fe-4f62-b8e8-5c3dbc87a52f",
        "0700abd9-5829-4c37-95d5-e5e3bd1bf004",
      ],
    }), // Fish Eye and Hannah
    defaultGenerationRatio: "r_1_1",
    prompt:
      "Portrait of the girl wearing a pink hoodie in a narrow ceramic shop.",
  },
  [PORTRAIT_BEN_UPSCALE_PRESET]: {
    modelPickerState: {
      isOpen: null,
    },
    defaultTool: {
      defaultCreateTool: undefined,
      defaultEditTool: "upscale",
    },
    modelUuids: [],
  },
  [PORTRAIT_JOHN_EXPAND_PRESET]: {
    modelPickerState: {
      isOpen: null,
    },
    defaultTool: {
      defaultCreateTool: undefined,
      defaultEditTool: "expand",
    },
    modelUuids: [],
    defaultGenerationRatio: "r_16_9",
  },

  // Stock Photography
  [STOCK_BRAINSTORMING_PRESET]: {
    selectedAssetUuid: undefined,
    modelPickerState: {
      isOpen: null,
    },
    defaultTool: {
      defaultCreateTool: "generate",
      defaultEditTool: undefined,
    },
    modelUuids: [PHOTO_REALISTIC_STYLE_MODEL_UUID],
    defaultGenerationRatio: "r_16_9",
    prompt:
      "A group of diverse professionals collaborating around a modern office table. Large windows, daylight, open space, smiling, smart-casual attire, tech devices, realistic photography.",
  },
  [STOCK_WOODSHOP_PRESET]: {
    selectedAssetUuid: undefined,
    modelPickerState: {
      isOpen: null,
    },
    defaultTool: {
      defaultCreateTool: "generate",
      defaultEditTool: undefined,
    },
    modelUuids: [HYPER_RESOLUTION_STYLE_MODEL_UUID],
    defaultGenerationRatio: "r_1_1",
    prompt:
      "A young professional creating a piece of furniture in a wood shop, relaxed and focused, photorealistic stock photo style.",
  },
  [STOCK_URBAN_PRESET]: {
    selectedAssetUuid: undefined,
    modelPickerState: {
      isOpen: null,
    },
    defaultTool: {
      defaultCreateTool: "brief",
      defaultEditTool: undefined,
    },
    modelUuids: getConfig({
      dev: [],
      staging: [],
      prod: ["4e80b5e1-1b5c-49c3-9b9b-3b4ad1397f6e"],
    }), // Exterior shot
    defaultGenerationRatio: undefined,
    prompt:
      "Trendy young adult crossing a city street, wearing headphones, urban buildings in background, natural light, energetic vibe, candid style stock photography.",
  },
  [STOCK_FOREST_PRESET]: {
    selectedAssetUuid: undefined,
    modelPickerState: {
      isOpen: null,
    },
    defaultTool: {
      defaultCreateTool: "generate",
      defaultEditTool: undefined,
    },
    modelUuids: getConfig({
      dev: [],
      staging: [],
      prod: ["e637a60a-7271-4423-baf9-b26f065d6837"],
    }), // Cinematic Warmth
    defaultGenerationRatio: "r_16_9",
    prompt:
      "Sunlight streaming through tall pine trees in a tranquil forest. Misty atmosphere, vibrant greens, golden hour. Horizontal landscape, wide angle, cinematic nature photography.",
  },
  [STOCK_BREAKFAST_PRESET]: {
    selectedAssetUuid: undefined,
    modelPickerState: {
      isOpen: null,
    },
    defaultTool: {
      defaultCreateTool: "generate",
      defaultEditTool: undefined,
    },
    modelUuids: getConfig({
      dev: [],
      staging: [],
      prod: ["f3cfab1a-aef5-4baa-b2e1-c3c0d5eea74c"],
    }), // Studio Shot
    defaultGenerationRatio: "r_1_1",
    prompt:
      "Flat lay of a healthy breakfast on a white table: smoothie bowl, whole grain toast, fruit, coffee, natural light, fresh and vibrant, minimalist food photography.",
  },
  [STOCK_3D_SHAPES_PRESET]: {
    selectedAssetUuid: undefined,
    modelPickerState: {
      isOpen: null,
    },
    defaultTool: {
      defaultCreateTool: "brief",
      defaultEditTool: undefined,
    },
    modelUuids: getConfig({
      dev: [],
      staging: [],
      prod: ["15e8f1c6-19c3-47d5-9280-59190c86338a"],
    }), // 3D Objects Studio
    defaultGenerationRatio: undefined,
    prompt:
      "A composition of colorful 3D geometric shapes, spheres, cylinders, and blocks, floating in a soft light scene with modern shadows. Minimal background, playful abstract style for visual storytelling.",
  },
  [STOCK_CARTOON_CHARACTER_PRESET]: {
    selectedAssetUuid: undefined,
    modelPickerState: {
      isOpen: null,
    },
    defaultTool: {
      defaultCreateTool: "generate",
      defaultEditTool: undefined,
    },
    modelUuids: getConfig({
      dev: [],
      staging: [],
      prod: ["9b2a27a1-9193-435f-8d16-0df8294f72b5"],
    }), // 3D Cartoon World
    defaultGenerationRatio: "r_4_5",
    prompt:
      "A 3D cartoon character, smiling, waving, with big expressive eyes, pastel colors, simple clothes, standing on a colorful platform, white background. Fun and friendly visual, suitable for kids media or playful branding.",
  },
  [STOCK_BREAKFAST_EDIT_PRESET]: {
    modelPickerState: {
      isOpen: null,
    },
    defaultTool: {
      defaultCreateTool: undefined,
      defaultEditTool: "edit-image-with-prompt",
    },
    modelUuids: [],
    prompt: "Replace the raspberries with strawberries.",
  },
  [STOCK_FOREST_EXPAND_PRESET]: {
    modelPickerState: {
      isOpen: null,
    },
    defaultTool: {
      defaultCreateTool: undefined,
      defaultEditTool: "expand",
    },
    modelUuids: [],
    defaultGenerationRatio: "r_1_1",
  },
  [STOCK_CARTOON_BACKGROUND_EDIT_PRESET]: {
    modelPickerState: {
      isOpen: null,
    },
    defaultTool: {
      defaultCreateTool: undefined,
      defaultEditTool: "edit-image-with-prompt",
    },
    modelUuids: [],
    prompt: "Replace the white environment with a kid playground area.",
  },

  // Studio Photoshoot presets
  [STUDIO_FOOD_EDITORIAL_PRESET]: {
    selectedAssetUuid: undefined,
    modelPickerState: {
      isOpen: null,
    },
    defaultTool: {
      defaultCreateTool: "generate",
      defaultEditTool: undefined,
    },
    modelUuids: [HYPER_RESOLUTION_STYLE_MODEL_UUID],
    defaultGenerationRatio: "r_4_5",
    prompt:
      "A perfectly plated gourmet dish photographed from above on a dark wooden table. The main course features grilled salmon with vibrant roasted vegetables, garnished with fresh herbs and a drizzle of golden olive oil. Warm, natural lighting creates appetizing shadows and highlights the textures and colors of the food.",
  },

  [STUDIO_COSMETIC_PRODUCT_PRESET]: {
    selectedAssetUuid: undefined,
    modelPickerState: {
      isOpen: null,
    },
    defaultTool: {
      defaultCreateTool: "generate",
      defaultEditTool: undefined,
    },
    defaultGenerationRatio: "r_16_9",
    prompt:
      "A white cosmetic cream jar sits elegantly on a green podium in a realistic studio setting. The background features a soft pale green tone, complemented by delicate olive leaves and their natural drop shadows. The overall composition evokes freshness and sophistication, perfect for a premium cosmetic advertisement.",
  },
  [STUDIO_OBJECT_STILL_LIFE_PRESET]: {
    selectedAssetUuid: undefined,
    modelPickerState: {
      isOpen: null,
    },
    defaultTool: {
      defaultCreateTool: "generate",
      defaultEditTool: undefined,
    },
    defaultGenerationRatio: "r_1_1",
    modelUuids: getConfig({
      dev: [],
      staging: [],
      // Totebag model (exists only in production for now)
      prod: ["0cd0e2b4-eebc-4479-a0a5-def55171c3ab"],
    }),
    prompt:
      "A stylish canvas tote bag with minimalist design sits elegantly on a marble surface, surrounded by carefully arranged everyday essentials like a laptop, notebooks, and fresh flowers. The scene is bathed in soft natural light streaming through a nearby window, creating gentle shadows that emphasize the bag's texture and practicality. The composition showcases the tote bag as both functional and fashionable, perfect for modern urban lifestyle.",
  },
  [STUDIO_CHARACTER_PORTRAIT_PRESET]: {
    selectedAssetUuid: undefined,
    modelPickerState: {
      isOpen: null,
    },
    defaultTool: {
      defaultCreateTool: "generate",
      defaultEditTool: undefined,
    },
    modelUuids: getConfig({
      dev: [],
      staging: [],
      // John model (exist only in production for now)
      prod: ["92ed6a47-a2a7-44fc-abcd-8abee650a4f4"],
    }),
    defaultGenerationRatio: "r_4_5",
    prompt:
      "A young adult male fitness professional exudes energy in a modern gym. With short, dark hair and wearing a white T-shirt, he symbolizes health and wellness. The gym features state-of-the-art equipment under natural lighting, creating a motivational atmosphere. This scene embodies an active lifestyle, perfect for showcasing fitness inspiration and physical well-being.",
  },
  [STUDIO_SOFT_FASHION_PRESET]: {
    selectedAssetUuid: undefined,
    modelPickerState: { isOpen: null },
    defaultTool: { defaultCreateTool: "generate", defaultEditTool: undefined },
    modelUuids: getConfig({
      dev: [],
      staging: [],
      prod: ["42ba35be-2aaf-46dd-b100-49d57404a99e"],
    }), // Studio Shot Person
    defaultGenerationRatio: "r_4_5",
    prompt:
      "A fashion portrait of a young model wearing a soft pastel outfit in a bright, airy photo studio. The lighting is gentle and flattering, with a seamless beige backdrop giving a premium editorial look reminiscent of Vogue. Focus on stylish clothing with crisp, elegant details.",
  },
  [STUDIO_EDITORIAL_CHARACTER_PRESET]: {
    selectedAssetUuid: undefined,
    modelPickerState: { isOpen: null },
    defaultTool: { defaultCreateTool: "generate", defaultEditTool: undefined },
    modelUuids: getConfig({
      dev: [],
      staging: [],
      prod: ["e794cace-adae-41ed-9af1-41e7f61fbf21"], // Adele
    }),
    defaultGenerationRatio: "r_4_5",
    prompt:
      "An editorial portrait of a young woman with light, natural makeup, wearing a casual outfit in a modern studio. Soft edge lighting, gentle color backgrounds, high-resolution details on face and hands.",
  },
  [STUDIO_PASTEL_OBJECT_PRESET]: {
    selectedAssetUuid: undefined,
    modelPickerState: { isOpen: null },
    defaultTool: { defaultCreateTool: "generate", defaultEditTool: undefined },
    modelUuids: getConfig({
      dev: [],
      staging: [],
      prod: ["f7ca640a-60e9-49c7-a88a-9c68a312981c"], // Pastel Oasis
    }),
    defaultGenerationRatio: "r_1_1",
    prompt:
      "An elegant perfume bottle displayed on a pastel-colored set with light turquoise and soft cream gradients, surrounded by fresh flowers. Sunlit studio effect and gentle shadows give it a clean, feminine vibe.",
  },
  [STUDIO_SHOES_HYPERREAL_PRESET]: {
    selectedAssetUuid: undefined,
    modelPickerState: { isOpen: null },
    defaultTool: { defaultCreateTool: "generate", defaultEditTool: undefined },
    modelUuids: getConfig({
      dev: [],
      staging: [],
      prod: ["a918d550-675d-419d-b1fa-61462d1a5854"], // Hyper Resolution
    }),
    defaultGenerationRatio: "r_1_1",
    prompt:
      "A hyper-realistic studio photograph of a pair of sneakers on a reflective black acrylic stage. Cliff-edge shadows and dramatic rim lighting add a premium, commercial look—perfect for a streetwear campaign.",
  },

  [STUDIO_NEOPOP_BAG_PRESET]: {
    selectedAssetUuid: undefined,
    modelPickerState: { isOpen: null },
    defaultTool: { defaultCreateTool: "generate", defaultEditTool: undefined },
    modelUuids: getConfig({
      dev: [],
      staging: [],
      prod: [
        "9688b28e-0692-492d-b0e9-a004d66b04f9",
        "0cd0e2b4-eebc-4479-a0a5-def55171c3ab",
      ],
    }), // Neo Pop + Tote Bag
    defaultGenerationRatio: "r_1_1",
    prompt:
      "A tote bag showcased on a vibrant, pop-colour background with playful geometric patterns and expressive neon lighting. Contemporary editorial style for a modern, young fashion brand.",
  },
  [STUDIO_WATERCOLOR_UNIVERSE_PRESET]: {
    selectedAssetUuid: undefined,
    modelPickerState: { isOpen: null },
    defaultTool: { defaultCreateTool: "generate", defaultEditTool: undefined },
    modelUuids: getConfig({
      dev: [],
      staging: [],
      prod: ["ece7566a-8152-4e61-8ba6-4743b180491e"], // Watercolor Universe
    }),
    defaultGenerationRatio: "r_16_9",
    prompt:
      "A dreamy watercolor illustration of a product on a floating pastel island, with gentle gradients and imaginative, celestial background. For a poetic campaign.",
  },
  [STUDIO_CINEMATIC_MONOCHROME_PRESET]: {
    selectedAssetUuid: undefined,
    modelPickerState: { isOpen: null },
    defaultTool: { defaultCreateTool: "generate", defaultEditTool: undefined },
    modelUuids: getConfig({
      dev: [],
      staging: [],
      prod: ["061ec6e4-e359-45d8-9558-ea2419a63f77"], // Cinematic Monochrome
    }),
    defaultGenerationRatio: "r_4_5",
    prompt:
      "A high-contrast black and white studio portrait with dramatic lighting, capturing deep shadows and crisp highlights for a timeless classic magazine look.",
  },
  [STUDIO_EXPAND_PRESET]: {
    modelPickerState: { isOpen: null },
    defaultTool: {
      defaultCreateTool: undefined,
      defaultEditTool: "expand",
    },
    modelUuids: [],
    defaultGenerationRatio: "r_16_9",
  },
  [STUDIO_UPSCALE_PRESET]: {
    modelPickerState: { isOpen: null },
    defaultTool: {
      defaultCreateTool: undefined,
      defaultEditTool: "upscale",
    },
    modelUuids: [],
  },
  [SOCIAL_AD_PRODUCT_PROMO_PRESET]: {
    modelPickerState: { isOpen: null },
    defaultTool: { defaultCreateTool: "generate", defaultEditTool: undefined },
    modelUuids: [],
    defaultGenerationRatio: "r_4_5",
    prompt:
      "A professionally photographed digital marketing ad image for a creative agency client. Features a modern, clean composition with a central product or lifestyle subject in sharp focus. Bright, natural lighting, styled props, and subtle brand colors are used to create an engaging visual. Includes empty space in the frame (e.g., upper third or side margin) for later text overlay by designers. Ideal for use in high-impact Instagram or Facebook ads promoting a product launch, seasonal offer, or brand awareness campaign.",
  },
  [SOCIAL_AD_BRIEF_BRAND_CAMPAIGN_PRESET]: {
    modelPickerState: { isOpen: null },
    defaultTool: { defaultCreateTool: "brief", defaultEditTool: undefined },
    prompt:
      "We are launching a new eco-friendly fashion line—generate a series of visuals for Instagram Stories using modern, fresh, and optimistic style. The campaign should attract urban Gen Z women.",
    modelUuids: [],
    defaultGenerationRatio: "r_9_16",
  },
  [SOCIAL_AD_STYLE_REF_PRESET]: {
    modelPickerState: { isOpen: null },
    defaultTool: { defaultCreateTool: "generate", defaultEditTool: undefined },
    modelUuids: getConfig({
      dev: [],
      staging: [],
      prod: ["4798ae69-06c2-4e25-886e-e13a6fc9cee7"], // Sleek
    }),
    defaultGenerationRatio: "r_4_5",
    prompt:
      "A beautifully composed, high-end lifestyle photograph in a sleek and modern aesthetic. The scene features a matte ivory tabletop set against a softly textured beige or taupe wall. On the table, a single, elegant product is centered — such as a minimal glass skincare bottle, a ceramic coffee cup with a dark roast, or a compact premium tech gadget. Complementary props include a rust-toned linen cloth, an emerald green plant in a charcoal pot, and a folded paper element in light brown or peach, arranged to suggest refined intentionality.",
  },
  [SOCIAL_AD_CHARACTER_PRODUCT_REF_PRESET]: {
    modelPickerState: { isOpen: null },
    defaultTool: { defaultCreateTool: "generate", defaultEditTool: undefined },
    // Example: John + Glasses
    modelUuids: getConfig({
      dev: [],
      staging: [],
      prod: [
        "92ed6a47-a2a7-44fc-abcd-8abee650a4f4", // John
        "da48d81d-7c3c-4c57-a22c-e391bfbde211", // Glasses
      ],
    }),
    defaultGenerationRatio: "r_4_5",
    prompt:
      "A striking portrait ad for urban glasses featuring John. City night background, ambient lighting, text overlay with brand slogan.",
  },
  [SOCIAL_AD_EDIT_EXISTING_PRESET]: {
    modelPickerState: { isOpen: null },
    defaultTool: {
      defaultCreateTool: undefined,
      defaultEditTool: "edit-image-with-prompt",
    },
    prompt: "Change the jacket color to pink",
    modelUuids: [],
  },
  [SOCIAL_AD_EXPAND_STORY_PRESET]: {
    modelPickerState: { isOpen: null },
    defaultTool: { defaultCreateTool: undefined, defaultEditTool: "expand" },
    defaultGenerationRatio: "r_191_100",
    modelUuids: [],
  },
  [SOCIAL_AD_UPSCALE_HD_PRESET]: {
    modelPickerState: { isOpen: null },
    defaultTool: { defaultCreateTool: undefined, defaultEditTool: "upscale" },
    modelUuids: [],
  },
} as const;
